'use client';

import { useCallback, useEffect, useRef } from 'react';
import { slugify } from '../../../lib/document-hub-utils';

interface TagDiscoveryHook {
  mergeDiscoveredFromFiles: (files: any[]) => void;
  resetDiscoveredTags: () => void;
}

export const useTagDiscovery = (
  onTagsDiscovered?: (tags: { id: string; label: string }[]) => void,
): TagDiscoveryHook => {
  const tagsCbRef = useRef<typeof onTagsDiscovered>(undefined);
  useEffect(() => {
    tagsCbRef.current = onTagsDiscovered;
  }, [onTagsDiscovered]);

  const discoveredTagsRef = useRef<Map<string, string>>(new Map());

  const emitDiscoveredTags = useCallback(() => {
    const cb = tagsCbRef.current;
    if (!cb) return;
    const list = Array.from(discoveredTagsRef.current.entries()).map(([id, label]) => ({ id, label }));
    cb(list);
  }, []);

  const mergeDiscoveredFromFiles = useCallback(
    (files: any[]) => {
      let changed = false;
      for (const f of files) {
        for (const t of f.documentTags ?? []) {
          const id = slugify(String(t?.name ?? ''));
          const label = String(t?.name ?? '');
          if (!id || !label) continue;
          if (!discoveredTagsRef.current.has(id)) {
            discoveredTagsRef.current.set(id, label);
            changed = true;
          }
        }
      }
      if (changed) emitDiscoveredTags();
    },
    [emitDiscoveredTags],
  );

  const resetDiscoveredTags = useCallback(() => {
    discoveredTagsRef.current = new Map();
    emitDiscoveredTags();
  }, [emitDiscoveredTags]);

  return {
    mergeDiscoveredFromFiles,
    resetDiscoveredTags,
  };
};
